import { yupResolver } from '@hookform/resolvers/yup';
import React from 'react';
import { useForm } from 'react-hook-form';
import { LuArrowLeft } from 'react-icons/lu';
import { useNavigate } from 'react-router-dom';

import { Role } from '@ndvr/auth-api';
import { StrategyType } from '@ndvr/portfolio-api';

import { Button } from '@/components/ui/button';
import { Page } from '@/components/ui/page';
import { useUserHasRole } from 'views/admin/dev-queries';

import { calculateTotalWeight, formatWeightForSave } from '../shared-utils';
import { createFormSchema, FormValues } from './strategy-form-schema';
import { useCreateStrategy, useListStrategies } from './strategy-queries';
import { StrategyFormBody } from './StrategyFormBody';
import { useAssetTableProps } from './useAssetTableProps';

export const NewStrategyPage: React.FC = () => {
    const navigate = useNavigate();
    const { data: hasSupportRole } = useUserHasRole(Role.SUPPORT_USER);
    const { data: strategies = [] } = useListStrategies();
    const { mutate: createStrategy, isPending: isSaving } = useCreateStrategy();

    const formSchema = createFormSchema(strategies.map((s) => s.strategyName));

    const form = useForm<FormValues>({
        resolver: yupResolver(formSchema),
        defaultValues: { strategyName: '', strategyType: StrategyType.PERSONAL, assetAllocations: [] },
        mode: 'onSubmit',
    });

    const assetTableProps = useAssetTableProps(form);
    const { assetAllocations } = assetTableProps;

    const handleBack = () => navigate('/model-library');

    const totalWeight = calculateTotalWeight(assetAllocations);
    const isNamed = form.watch('strategyName').trim().length > 0;
    const isValid = isNamed && assetAllocations.length > 0 && totalWeight === 100;

    const validationMessage = !isNamed
        ? 'Name the strategy to save.'
        : assetAllocations.length === 0 || totalWeight !== 100
          ? 'Weights must total 100% to save.'
          : undefined;

    const handleSave = (data: FormValues) => {
        createStrategy(
            {
                strategyName: data.strategyName,
                strategyType: data.strategyType,
                assetAllocations: data.assetAllocations.map((asset) => ({
                    ...asset,
                    weight: formatWeightForSave(asset.weight),
                })),
            },
            { onSuccess: handleBack },
        );
    };

    return (
        <Page className="tracking-[-0.02em]">
            <div className="border-cocoa-200 sticky top-0 z-10 flex min-h-16 items-center gap-4 border-b bg-white px-8">
                <button
                    type="button"
                    onClick={handleBack}
                    className="text-cocoa-500 hover:text-cocoa-1000 flex items-center gap-1.5 text-sm font-medium"
                >
                    <LuArrowLeft className="h-4 w-4" /> Model Library
                </button>
                <span className="text-cocoa-300 text-sm">/</span>
                <span className="text-cocoa-700 text-sm">Strategy</span>
                <div className="ml-auto flex items-center gap-3">
                    {validationMessage && <span className="text-sm text-red-600">{validationMessage}</span>}
                    <Button
                        variant="outline"
                        type="button"
                        className="font-medium"
                        onClick={handleBack}
                        disabled={isSaving}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="default"
                        type="submit"
                        className="font-medium"
                        form="new-strategy-form"
                        disabled={!isValid || isSaving}
                    >
                        {isSaving ? 'Saving...' : 'Save strategy'}
                    </Button>
                </div>
            </div>

            <form id="new-strategy-form" onSubmit={form.handleSubmit(handleSave)} className="overflow-y-auto p-8 pb-24">
                <StrategyFormBody
                    form={form}
                    showTypeSelector={!!hasSupportRole}
                    canChangeType
                    autoFocusName
                    {...assetTableProps}
                />
            </form>
        </Page>
    );
};
